import type { Node, XYPosition } from 'reactflow';
import { DEFAULT_NODE_SIZE } from './boardConstants';
import type { BoardNodeType } from '../../types';

/** Screen-pixel distance before an edge/center snaps. Converted to flow units with / zoom. */
export const SNAP_SCREEN_PX = 8;

export interface SnapRect {
  id: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
  cx: number;
  cy: number;
}

export interface SnapGuide {
  orientation: 'v' | 'h';
  position: number;
  start: number;
  end: number;
}

export interface SnapResult {
  dx: number;
  dy: number;
  guides: SnapGuide[];
}

function nodeSize(node: Node): { width: number; height: number } {
  const type = node.type as BoardNodeType;
  const defaults = DEFAULT_NODE_SIZE[type] || { width: 200, height: 200 };
  return {
    width: Number(node.width ?? node.style?.width ?? defaults.width) || defaults.width,
    height: Number(node.height ?? node.style?.height ?? defaults.height) || defaults.height,
  };
}

export function absoluteNodePosition(node: Node, nodes: Node[]): XYPosition {
  let x = node.position.x;
  let y = node.position.y;
  let parentId = node.parentId;
  while (parentId) {
    const parent = nodes.find((n) => n.id === parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    parentId = parent.parentId;
  }
  return { x, y };
}

export function nodeToSnapRect(node: Node, nodes: Node[]): SnapRect {
  const abs = absoluteNodePosition(node, nodes);
  const { width, height } = nodeSize(node);
  return {
    id: node.id,
    left: abs.x,
    top: abs.y,
    right: abs.x + width,
    bottom: abs.y + height,
    cx: abs.x + width / 2,
    cy: abs.y + height / 2,
  };
}

export function unionSnapRects(rects: SnapRect[]): SnapRect | null {
  if (rects.length === 0) return null;
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return {
    id: 'selection',
    left,
    top,
    right,
    bottom,
    cx: (left + right) / 2,
    cy: (top + bottom) / 2,
  };
}

function xEdges(rect: SnapRect): number[] {
  return [rect.left, rect.cx, rect.right];
}

function yEdges(rect: SnapRect): number[] {
  return [rect.top, rect.cy, rect.bottom];
}

function bestAxisSnap(
  moving: number[],
  targets: Array<{ value: number; rect: SnapRect }>,
  threshold: number,
): { delta: number; line: number; matches: SnapRect[] } | null {
  let best: { dist: number; delta: number; line: number } | null = null;
  for (const m of moving) {
    for (const t of targets) {
      const delta = t.value - m;
      const dist = Math.abs(delta);
      if (dist > threshold) continue;
      if (!best || dist < best.dist) {
        best = { dist, delta, line: t.value };
      }
    }
  }
  if (!best) return null;
  const matches = targets.filter((t) => Math.abs(t.value - best!.line) < 0.5).map((t) => t.rect);
  return { delta: best.delta, line: best.line, matches };
}

function guideRange(moving: SnapRect, matches: SnapRect[], horizontal: boolean): { start: number; end: number } {
  const a = horizontal
    ? [moving.left, moving.right, ...matches.flatMap((r) => [r.left, r.right])]
    : [moving.top, moving.bottom, ...matches.flatMap((r) => [r.top, r.bottom])];
  const pad = 12;
  return { start: Math.min(...a) - pad, end: Math.max(...a) + pad };
}

export function snapMovingRect(moving: SnapRect, others: SnapRect[], threshold: number): SnapResult {
  if (others.length === 0 || threshold <= 0) {
    return { dx: 0, dy: 0, guides: [] };
  }

  const xTargets = others.flatMap((rect) => xEdges(rect).map((value) => ({ value, rect })));
  const yTargets = others.flatMap((rect) => yEdges(rect).map((value) => ({ value, rect })));

  const xSnap = bestAxisSnap(xEdges(moving), xTargets, threshold);
  const ySnap = bestAxisSnap(yEdges(moving), yTargets, threshold);

  const dx = xSnap?.delta ?? 0;
  const dy = ySnap?.delta ?? 0;
  const snapped: SnapRect = {
    ...moving,
    left: moving.left + dx,
    right: moving.right + dx,
    cx: moving.cx + dx,
    top: moving.top + dy,
    bottom: moving.bottom + dy,
    cy: moving.cy + dy,
  };

  const guides: SnapGuide[] = [];
  if (xSnap) {
    const { start, end } = guideRange(snapped, xSnap.matches, false);
    guides.push({ orientation: 'v', position: xSnap.line, start, end });
  }
  if (ySnap) {
    const { start, end } = guideRange(snapped, ySnap.matches, true);
    guides.push({ orientation: 'h', position: ySnap.line, start, end });
  }

  return { dx, dy, guides };
}

/** Nodes RF actually translates during a multi-drag (skip children of a moving parent). */
export function independentlyMoved(node: Node, movingIds: Set<string>): boolean {
  return !node.parentId || !movingIds.has(node.parentId);
}

const SNAP_STORAGE_KEY = 'lctf-board-snap-enabled';

export function loadSnapEnabled(): boolean {
  try {
    const raw = localStorage.getItem(SNAP_STORAGE_KEY);
    if (raw === '0') return false;
    if (raw === '1') return true;
  } catch {
    /* ignore */
  }
  return true;
}

export function saveSnapEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SNAP_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    /* ignore */
  }
}
